import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import HelpPopover from '../../HelpPopover';
import * as monaco from 'monaco-editor';
import { editor, Range } from 'monaco-editor';
import { getMainProgramEditorProxy } from '../utils/mainProgramEditor';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/ReduxHooks';
import type { ActionItem } from '../../../redux/EditorSlice';
import { ARG_KEYS, DRIVE_MODE, type DriveMode, MATCH_TIME_DEFAULTS, ROBOT_PROP, RUNMODE } from '../../../utils/constants';
import ActionSelector from '../../Drawers/Controller/ActionSelector';
import FrameworkActionArgumentFields from '../../Drawers/Controller/FrameworkActionArgumentFields';
import type { FrameworkArgument } from '../../../framework/framework.d';
import { getEnumOptions } from '../../../framework/framework-enums';
import type { Path } from '../../PathBuilder/pathBuilderTypes';
import {
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  isTupleArg,
  sequenceTupleRowsToArgValues,
  tupleMemberSchemaDefault,
  tupleMemberValueKey,
} from '../../../utils/tupleArg';
import { frameworkActionArgsAreValid } from '../../../utils/sequenceTupleValidation';
import type { AutoZeroSensorActionContext } from '../../../utils/autoZeroSensorActionArgs';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import { findMatchingDelimiterIndex, offsetAtLineColumn, splitCodeLines } from '../../../utils/pythonSourceScanUtils';
import RoutineStepEditor, {
  appendChildAtPath,
  createEmptyGroupStep,
  moveStepToPosition,
  pruneRoutineStep,
  removeStepAtPath,
  routineStepArgsAreValid,
  snapshotRoutineDraft,
  updateStepAtPath,
  type RoutineStepDropPosition,
} from './AutonomousRoutineStepEditor';
import {
  appendRoutineToCode,
  cloneRawActionArgValues,
  clearRawActionArgValue,
  collectTopLevelPythonIdentifiers,
  deleteRoutineFromCode,
  DO_NOTHING_AUTONOMOUS_ROUTINE_ID,
  extractCallArgsText,
  formatActionExpression,
  formatAutonomousRoutineAssignment,
  getAutonomousRoutineActionExpression,
  getRawActionArgValue,
  isHiddenRuntimeActionArg,
  type AutonomousRoutineOption,
  type AutonomousRoutineStep,
  findActionDefForLabel,
  findProgramStartActionCalls,
  makeUniqueRoutineIdentifier,
  replaceRoutineInCode,
  scanAutonomousRoutinesFromCode,
  setRawActionArgValue,
  splitTopLevelByComma,
  updateRawActionArgValue,
  withHiddenRuntimeActionArgs,
} from './driveModeAutonomousActionUtils';
import { frameworkArgValueToFormString, isFrameworkArgOptionalNoneDefault, isFrameworkNumberArgType } from '../../../utils/frameworkArgTypes';
import { isUnsupportedSequenceTupleLiteral, parseSequenceTupleLiteral, sequenceTupleLiteralRowsAreEditable } from '../../Drawers/pythonArgParser';
import { useAllAvailableActions } from '../../../hooks/editor/useAllAvailableActions';
import { excludeGroupOnlyActions, type ProgramStartActionConfig } from './hardwareParser';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';

const enumOptions = getEnumOptions();

const isFollowPathAction = (name: string | undefined): boolean => {
  return name === 'follow_path_action';
};

const smallOutlinedFieldSx = {
  '& .MuiInputBase-root': { fontSize: 12 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 8 },
} as const;

const matchTimeFieldSx = {
  width: 125,
  ...smallOutlinedFieldSx,
} as const;

const modeRadioLabelSx = {
  alignItems: 'flex-start',
  my: 0.25,
  ml: -0.5,
  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
} as const;

const DRIVE_MODE_OPTIONS = [
  {
    value: DRIVE_MODE.DRIVE_CONTROL,
    labelKey: 'driveModeConfig.drive-control',
    defaultLabel: 'Drive Control',
    popoverId: 'drive-control-info-popover',
    infoKey: 'driveModeConfigDrawer.drive-control-info',
    infoDefault: 'Drive control information',
  },
  {
    value: DRIVE_MODE.AUTONOMOUS,
    labelKey: 'driveModeConfig.autonomous',
    defaultLabel: 'Autonomous',
    popoverId: 'autonomous-info-popover',
    infoKey: 'driveModeConfigDrawer.autonomous-info',
    infoDefault: 'Autonomous information',
  },
  {
    value: DRIVE_MODE.MATCH,
    labelKey: 'driveModeConfig.match',
    defaultLabel: 'Match',
    popoverId: 'match-info-popover',
    infoKey: 'driveModeConfigDrawer.match-info',
    infoDefault: 'Match information',
  },
] as const;

const CODE_EDITED_AUTONOMOUS_ROUTINE_ID = '__code_edited_autonomous_action__';

interface DriveModeConfigurationTabProps {
  editor?: editor.IStandaloneCodeEditor;
}

type ProgramStartActionDraft = ProgramStartActionConfig & {
  draftId: string;
};

const DriveModeConfigurationTab: React.FC<DriveModeConfigurationTabProps> = ({ editor: editorProp }) => {
  const [driveMode, setDriveMode] = useState<DriveMode>(DRIVE_MODE.DRIVE_CONTROL);
  const [autonomousTime, setAutonomousTime] = useState('');
  const [driveControlTime, setDriveControlTime] = useState('');
  const { t } = useTranslation();
  const [programStartActions, setProgramStartActions] = useState<ProgramStartActionDraft[]>([]);
  const [programStartDialogOpen, setProgramStartDialogOpen] = useState(false);
  const [programStartDialogActions, setProgramStartDialogActions] = useState<ProgramStartActionDraft[]>([]);
  const [autonomousRoutines, setAutonomousRoutines] = useState<AutonomousRoutineOption[]>([]);
  const [selectedAutonomousRoutineId, setSelectedAutonomousRoutineId] = useState(DO_NOTHING_AUTONOMOUS_ROUTINE_ID);
  const [routineDialogOpen, setRoutineDialogOpen] = useState(false);
  const [routineDialogMode, setRoutineDialogMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [routineDialogSourceId, setRoutineDialogSourceId] = useState<string | null>(null);
  const [routineDialogBaseline, setRoutineDialogBaseline] = useState<string | null>(null);
  const [routineDraftName, setRoutineDraftName] = useState('autonomous_routine');
  const [routineDraftComposition, setRoutineDraftComposition] = useState<AutonomousRoutineStep>(createEmptyGroupStep());
  const [legacyAutonomousActionSelection, setLegacyAutonomousActionSelection] = useState('');
  const [rawAutonomousActionCode, setRawAutonomousActionCode] = useState('');
  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const editorActionList = useAppSelector((state) => state.editor.actionList) as ActionItem[];
  const allAvailableActionsForDef = useAllAvailableActions();
  const programStartSelectableActions = useMemo(() => excludeGroupOnlyActions(editorActionList), [editorActionList]);
  const startCallConfig = useAppSelector((state) => state.editor.startCallConfig);
  const project = useAppSelector((state) => state.project.loadedProject);
  const pathOptions = useMemo<Array<{ id: string; name: string }>>(
    () => (project?.paths ?? []).map((p: Path) => ({ id: p.id, name: p.name })),
    [project?.paths]
  );
  // Argument validation must resolve sensor-specific args against the same source the fields do.
  const autoZeroSensorContext = useMemo<AutoZeroSensorActionContext>(() => ({ programCode: project?.program?.code ?? '' }), [project?.program?.code]);
  const isProgrammaticEdit = useRef(false);
  const signatureDefaultsRef = useRef<{ auto: string; driver: string }>({ auto: MATCH_TIME_DEFAULTS.AUTO, driver: MATCH_TIME_DEFAULTS.DRIVER });
  const programStartDraftIdRef = useRef(0);

  const resolveActionDef = useCallback((label: string) => findActionDefForLabel(allAvailableActionsForDef, label), [allAvailableActionsForDef]);
  const legacyAutonomousActionDef = useMemo(
    () => findActionDefForLabel(allAvailableActionsForDef, legacyAutonomousActionSelection),
    [allAvailableActionsForDef, legacyAutonomousActionSelection]
  );
  const isUnsupportedLegacyAutonomousAction = useMemo(() => {
    if (!rawAutonomousActionCode.trim()) return false;
    if (!legacyAutonomousActionSelection) return true;
    if (legacyAutonomousActionSelection.startsWith('Custom.')) return false;
    return !legacyAutonomousActionDef;
  }, [legacyAutonomousActionDef, legacyAutonomousActionSelection, rawAutonomousActionCode]);

  const nextProgramStartDraftId = useCallback(() => {
    programStartDraftIdRef.current += 1;
    return `program-start-action-${programStartDraftIdRef.current}`;
  }, []);

  const createProgramStartDraft = useCallback(
    (action?: Partial<ProgramStartActionConfig>): ProgramStartActionDraft => {
      const argValues = cloneRawActionArgValues(action?.argValues);
      const rawExpression = String(action?.rawExpression ?? '').trim();
      return {
        draftId: nextProgramStartDraftId(),
        selection: String(action?.selection ?? ''),
        argValues,
        rawExpression: rawExpression || undefined,
      };
    },
    [nextProgramStartDraftId]
  );

  const getEditor = useCallback(
    () => getMainProgramEditorProxy(editorProp ?? (window.__bps_editor as editor.IStandaloneCodeEditor | null | undefined)),
    [editorProp]
  );

  const getModelText = useCallback((model: monaco.editor.ITextModel): string => {
    if (typeof model.getValue === 'function') return model.getValue();
    return Array.from({ length: model.getLineCount() }, (_, index) => model.getLineContent(index + 1)).join('\n');
  }, []);

  const replaceModelText = useCallback(
    (nextText: string) => {
      const editor = getEditor();
      const model = editor?.getModel();
      if (!editor || !model) return;
      isProgrammaticEdit.current = true;
      editor.pushUndoStop();
      editor.executeEdits('runSetup.replaceModelText', [
        {
          range: new Range(1, 1, model.getLineCount(), model.getLineMaxColumn(model.getLineCount())),
          text: nextText,
          forceMoveMarkers: true,
        },
      ]);
      editor.pushUndoStop();
      setAutonomousRoutines(scanAutonomousRoutinesFromCode(nextText));
      setTimeout(() => {
        isProgrammaticEdit.current = false;
      }, 0);
    },
    [getEditor]
  );

  const selectedAutonomousRoutine = useMemo(
    () => autonomousRoutines.find((routine) => routine.id === selectedAutonomousRoutineId) ?? null,
    [autonomousRoutines, selectedAutonomousRoutineId]
  );

  const setAutonomousRoutinesIfChanged = useCallback((next: AutonomousRoutineOption[]) => {
    const snapshot = (items: AutonomousRoutineOption[]) =>
      JSON.stringify(
        items.map((routine) => ({
          id: routine.id,
          identifier: routine.identifier,
          label: routine.label,
          source: routine.source,
          editable: routine.editable,
          deletable: routine.deletable,
          duplicable: routine.duplicable,
          composition: routine.composition,
          expression: routine.expression,
          referenceExpression: routine.referenceExpression,
          startLineIndex: routine.startLineIndex,
          endLineIndex: routine.endLineIndex,
        }))
      );
    setAutonomousRoutines((prev) => (snapshot(prev) === snapshot(next) ? prev : next));
  }, []);

  const getAutonomousActionExpressionForRoutineId = useCallback(
    (routineId: string): string => {
      if (routineId === DO_NOTHING_AUTONOMOUS_ROUTINE_ID) return '';
      const routine = autonomousRoutines.find((candidate) => candidate.id === routineId);
      return getAutonomousRoutineActionExpression(routine) || routineId;
    },
    [autonomousRoutines]
  );

  const resolveSelectedRoutineIdFromStartConfig = useCallback(
    (
      routines: AutonomousRoutineOption[],
      autoSelection: string,
      autoArgValues: Record<string, string>,
      autonomousActionExpression?: string
    ): { routineId: string; routines: AutonomousRoutineOption[] } => {
      const rawExpression = autonomousActionExpression?.trim();
      if (!autoSelection && !rawExpression) return { routineId: DO_NOTHING_AUTONOMOUS_ROUTINE_ID, routines };
      const identifier = autoSelection.startsWith('Custom.') ? autoSelection.slice('Custom.'.length) : autoSelection;
      const routine = routines.find((candidate) => candidate.identifier === identifier || candidate.id === identifier);
      if (routine) return { routineId: routine.id, routines };

      const expression = rawExpression || formatActionExpression(autoSelection, autoArgValues, resolveActionDef);
      if (!expression) return { routineId: DO_NOTHING_AUTONOMOUS_ROUTINE_ID, routines };
      const inlineRoutine: AutonomousRoutineOption = {
        id: CODE_EDITED_AUTONOMOUS_ROUTINE_ID,
        identifier: '',
        label: '',
        source: 'code-only',
        editable: false,
        deletable: false,
        duplicable: false,
        composition: null,
        expression,
        startLineIndex: -1,
        endLineIndex: -1,
      };
      return { routineId: inlineRoutine.id, routines: [...routines, inlineRoutine] };
    },
    [resolveActionDef]
  );

  const normalizeArgValuesForAction = useCallback(
    (selection: string, values: Record<string, string>): Record<string, string> => {
      const actionDef = withHiddenRuntimeActionArgs(findActionDefForLabel(allAvailableActionsForDef, selection));
      const argList = actionDef?.arguments;
      if (!actionDef) {
        const next = cloneRawActionArgValues(values);
        return next;
      }
      if (!argList?.length) return {};

      const next = cloneRawActionArgValues(values);
      const allowedKeys = new Set<string>();
      const followPathNeedsPathArg = isFollowPathAction(actionDef.name) && argList.some((a) => a.name === 'path');
      for (const [index, arg] of argList.entries()) {
        const positionalKey = `arg${index}`;
        const frameworkArg = arg as FrameworkArgument;

        if (isSequenceTupleArg(frameworkArg)) {
          // Expand a `[(...), ...]` literal into indexed row keys. An unparseable expression
          // (e.g. `get_lines()`) is preserved verbatim under the arg key so it renders as
          // unsupported code rather than being silently dropped. Empty / `[]` => no rows.
          // The arg key is allow-listed so a preserved raw value survives the cleanup pass below.
          allowedKeys.add(frameworkArg.name);
          const literal = next[arg.name] ?? next[positionalKey];
          delete next[positionalKey];
          delete next[arg.name];
          if (typeof literal === 'string' && literal.trim().length > 0) {
            const seqMembers = frameworkArg.members ?? [];
            if (sequenceTupleLiteralRowsAreEditable(literal, seqMembers)) {
              Object.assign(next, sequenceTupleRowsToArgValues(frameworkArg, parseSequenceTupleLiteral(literal)));
            } else if (isUnsupportedSequenceTupleLiteral(literal, seqMembers)) {
              next[arg.name] = literal.trim();
            }
          }
          continue;
        }

        if (next[arg.name] === undefined && next[positionalKey] !== undefined) {
          next[arg.name] = String(next[positionalKey] ?? '');
          const rawPositionalValue = getRawActionArgValue(values, positionalKey);
          if (rawPositionalValue !== undefined) setRawActionArgValue(next, arg.name, rawPositionalValue);
          delete next[positionalKey];
          clearRawActionArgValue(next, positionalKey);
        }

        allowedKeys.add(frameworkArg.name);
        if (isTupleArg(frameworkArg)) {
          const hasTupleRawValue =
            getRawActionArgValue(next, frameworkArg.name) !== undefined ||
            (Object.prototype.hasOwnProperty.call(next, frameworkArg.name) && String(next[frameworkArg.name] ?? '').trim().length > 0);
          for (const [memberIndex, member] of (frameworkArg.members || []).entries()) {
            const memberArg = member as FrameworkArgument;
            const memberKey = tupleMemberValueKey(frameworkArg.name, memberArg.name);
            allowedKeys.add(memberKey);
            if (!hasTupleRawValue && next[memberKey] === undefined) {
              const enumOpts = isFrameworkNumberArgType(memberArg.type || '') ? undefined : enumOptions[memberArg.type || ''];
              const defaultValue = tupleMemberSchemaDefault(frameworkArg, memberIndex, memberArg);
              if (defaultValue !== undefined && defaultValue !== null && String(defaultValue) !== '') {
                next[memberKey] = frameworkArgValueToFormString(memberArg, defaultValue);
              } else if (enumOpts?.length) next[memberKey] = enumOpts[0].value;
              else next[memberKey] = '';
            }
          }
          continue;
        }
        if (next[arg.name] === undefined) {
          if (isHiddenRuntimeActionArg(frameworkArg)) continue;
          const enumOpts = isFrameworkNumberArgType(arg.type || '') ? undefined : enumOptions[arg.type || ''];
          const defaultValue = frameworkArg.defaultValue ?? frameworkArg.value;
          if (isFrameworkArgOptionalNoneDefault(frameworkArg)) next[arg.name] = '';
          else if (defaultValue !== undefined && defaultValue !== null && String(defaultValue) !== '') {
            next[arg.name] = frameworkArgValueToFormString(frameworkArg, defaultValue);
          } else if (enumOpts?.length) next[arg.name] = enumOpts[0].value;
          else next[arg.name] = '';
        }
      }
      for (const k of Object.keys(next)) {
        if (allowedKeys.has(k)) continue;
        // Keep indexed sequence-of-tuples row keys (`<argName>.<row>.<member>`).
        if (argList.some((a) => isSequenceTupleArg(a) && k.startsWith(`${a.name}.`))) continue;
        // Keep hidden/runtime kwargs that are not currently exposed in the visible schema.
        if (/^[A-Za-z_][\w]*$/.test(k)) continue;
        delete next[k];
        clearRawActionArgValue(next, k);
      }
      if (followPathNeedsPathArg) {
        const current = String(next.path ?? '');
        const rawSourcePath = getRawActionArgValue(next, 'path');
        if (!current && pathOptions.length > 0) next.path = pathOptions[0].name;
        else if (current && !rawSourcePath && !pathOptions.some((p) => p.name === current)) next.path = '';
      }
      return next;
    },
    [allAvailableActionsForDef, pathOptions]
  );

  const getRoutineDisplayLabel = useCallback(
    (routine: AutonomousRoutineOption | null | undefined): string => {
      if (!routine) return '';
      if (routine.id === CODE_EDITED_AUTONOMOUS_ROUTINE_ID) {
        return t('driveModeConfig.autonomousRoutines.codeEditedLabel', { defaultValue: 'Code-edited autonomous action' });
      }
      return routine.label;
    },
    [t]
  );

  const selectedAutonomousRoutineLabel = getRoutineDisplayLabel(selectedAutonomousRoutine);
  const prunedRoutineDraftComposition = useMemo(() => pruneRoutineStep(routineDraftComposition), [routineDraftComposition]);
  const routineDraftSnapshot = useMemo(
    () => snapshotRoutineDraft(routineDraftName, prunedRoutineDraftComposition),
    [prunedRoutineDraftComposition, routineDraftName]
  );
  const routineDialogIsDirty = routineDialogMode !== 'edit' || routineDialogBaseline === null || routineDraftSnapshot !== routineDialogBaseline;
  const routineDraftArgsAreValid = Boolean(
    prunedRoutineDraftComposition && routineStepArgsAreValid(prunedRoutineDraftComposition, allAvailableActionsForDef, t, autoZeroSensorContext)
  );
  const routineDialogCanSave = Boolean(prunedRoutineDraftComposition) && routineDialogIsDirty && routineDraftArgsAreValid;
  // Block saving while action argument validation shown inline has failures.
  const programStartDialogCanSave = useMemo(
    () =>
      programStartDialogActions.every((action) => {
        if (!action.selection) return true;
        const actionDef = findActionDefForLabel(allAvailableActionsForDef, action.selection);
        return frameworkActionArgsAreValid(actionDef, action.argValues, t, autoZeroSensorContext);
      }),
    [programStartDialogActions, allAvailableActionsForDef, autoZeroSensorContext, t]
  );
  const addAutonomousRoutineLabel = t('driveModeConfig.autonomousRoutines.addAria', 'add autonomous routine');
  const editAutonomousRoutineLabel = t('driveModeConfig.autonomousRoutines.editAria', {
    routine: selectedAutonomousRoutineLabel,
    defaultValue: 'edit autonomous routine',
  });
  const duplicateAutonomousRoutineLabel = t('driveModeConfig.autonomousRoutines.duplicateAria', {
    routine: selectedAutonomousRoutineLabel,
    defaultValue: 'duplicate autonomous routine',
  });
  const deleteAutonomousRoutineLabel = t('driveModeConfig.autonomousRoutines.deleteAria', {
    routine: selectedAutonomousRoutineLabel,
    defaultValue: 'delete autonomous routine',
  });
  const addProgramStartActionLabel = t('driveModeConfig.programStartActions.addAria', 'add program start action');

  const updateArgValuesForAction = useCallback(
    (selection: string, values: Record<string, string>, name: string, value: string): Record<string, string> => {
      const next = updateRawActionArgValue(values, name, value);
      const actionDef = findActionDefForLabel(allAvailableActionsForDef, selection);
      const tupleArg = (actionDef?.arguments || []).find((arg) =>
        isTupleArg(arg as FrameworkArgument)
          ? (arg as FrameworkArgument).members?.some((member) => tupleMemberValueKey(arg.name, member.name) === name)
          : false
      ) as FrameworkArgument | undefined;
      if (tupleArg) {
        delete next[tupleArg.name];
        clearRawActionArgValue(next, tupleArg.name);
      }
      return next;
    },
    [allAvailableActionsForDef]
  );

  const handleDriveMode = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const value = (event.target as HTMLInputElement).value as DriveMode;
    setDriveMode(value);
    // Update Python start(...) arguments
    updateStartArgsInCode({
      nextDriveMode: value,
      autonomousTimeStr: autonomousTime,
      driveControlTimeStr: driveControlTime,
      autonomousRoutineId: value === DRIVE_MODE.DRIVE_CONTROL ? DO_NOTHING_AUTONOMOUS_ROUTINE_ID : selectedAutonomousRoutineId,
    });
  };

  const handleAutonomousTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const value = (event.target as HTMLInputElement).value;
    setAutonomousTime(value);
    updateStartArgsInCode({ autonomousTimeStr: value, nextDriveMode: driveMode });
  };

  const handleDriveTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const value = (event.target as HTMLInputElement).value;
    setDriveControlTime(value);
    updateStartArgsInCode({ driveControlTimeStr: value, nextDriveMode: driveMode });
  };

  type StartCallRange = {
    name: string;
    startLine: number;
    argsStartCol: number;
    argsEndLine: number;
    argsEndCol: number;
    argsText: string;
  };

  type ProgramStartActionCallRange = {
    startLine: number;
    endLine: number;
  };

  type TextEditOperation = {
    startLine: number;
    startColumn: number;
    endLine: number;
    endColumn: number;
    text: string;
  };

  const lineColumnAtOffset = (lines: string[], offset: number): { lineIndex: number; column: number } => {
    let remaining = offset;
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const lineLength = lines[lineIndex]?.length ?? 0;
      if (remaining <= lineLength) return { lineIndex, column: remaining };
      remaining -= lineLength + 1;
    }
    const lastLineIndex = Math.max(lines.length - 1, 0);
    return { lineIndex: lastLineIndex, column: lines[lastLineIndex]?.length ?? 0 };
  };

  /**
   * Finds all robot.start(...) or RobotBase().start(...) calls and returns their ranges (supports multiline).
   * Used to detect duplicates and delete extras so only one remains.
   */
  const findAllStartCallRangesInCode = (code: string): StartCallRange[] => {
    const results: StartCallRange[] = [];
    const lines = splitCodeLines(code);
    const normalizedCode = lines.join('\n');
    let lineNum = 1;
    while (lineNum <= lines.length) {
      const lineIndex = lineNum - 1;
      const line = lines[lineIndex] ?? '';
      const startMatch = line.match(/^\s*([A-Za-z_][\w]*)(?:\s*\(\s*\))?\s*\.\s*start\s*\(\s*/);
      if (!startMatch) {
        lineNum++;
        continue;
      }
      const robotName = startMatch[1];
      const dotStartIdx = line.search(/\.\s*start\s*\(/);
      const openParenIdx = line.indexOf('(', dotStartIdx);
      if (openParenIdx < 0) {
        lineNum++;
        continue;
      }
      const argsStartCol1Based = openParenIdx + 2;
      const openParenOffset = offsetAtLineColumn(lines, lineIndex, openParenIdx);
      const closeParenOffset = findMatchingDelimiterIndex(normalizedCode, openParenOffset, '(', ')');
      const extracted = extractCallArgsText(lines, lineIndex, openParenIdx);
      if (closeParenOffset === -1 || !extracted) {
        lineNum++;
        continue;
      }
      const closePosition = lineColumnAtOffset(lines, closeParenOffset);
      results.push({
        name: robotName,
        startLine: lineNum,
        argsStartCol: argsStartCol1Based,
        argsEndLine: closePosition.lineIndex + 1,
        argsEndCol: closePosition.column + 1,
        argsText: extracted.argsText,
      });
      lineNum = closePosition.lineIndex + 2;
    }
    return results;
  };

  const findProgramStartActionCallRangesInCode = (code: string): ProgramStartActionCallRange[] => {
    const lines = splitCodeLines(code);
    return findProgramStartActionCalls(lines).map((call) => ({
      startLine: call.startLineIndex + 1,
      endLine: call.endLineIndex + 1,
    }));
  };

  const rangeOffsetInLines = (lines: string[], line: number, column: number): number => {
    if (line > lines.length) return lines.join('\n').length;
    return offsetAtLineColumn(lines, Math.max(0, line - 1), Math.max(0, column - 1));
  };

  const applyTextEditsToCode = (code: string, edits: TextEditOperation[]): string => {
    const lines = splitCodeLines(code);
    const normalizedCode = lines.join('\n');
    return [...edits]
      .sort((a, b) => rangeOffsetInLines(lines, b.startLine, b.startColumn) - rangeOffsetInLines(lines, a.startLine, a.startColumn))
      .reduce((nextCode, edit) => {
        const start = rangeOffsetInLines(lines, edit.startLine, edit.startColumn);
        const end = rangeOffsetInLines(lines, edit.endLine, edit.endColumn);
        return `${nextCode.slice(0, start)}${edit.text}${nextCode.slice(end)}`;
      }, normalizedCode);
  };

  const ensureModeImport = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _model: monaco.editor.ITextModel,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _edits: monaco.editor.IIdentifiedSingleEditOperation[]
  ) => {
    // No-op: RunMode is available from top-level sphero import in user templates.
  };

  const mapDriveModeToModeToken = (mode: string): string => {
    switch (mode) {
      case DRIVE_MODE.AUTONOMOUS:
        return RUNMODE.AUTONOMOUS;
      case DRIVE_MODE.MATCH:
        return RUNMODE.MATCH;
      case DRIVE_MODE.DRIVE_CONTROL:
      default:
        return RUNMODE.DRIVER;
    }
  };

  const parseStartArgs = (
    argsText: string
  ): {
    modeToken?: string;
    autoTime?: string;
    driverTime?: string;
    autonomousAction?: string;
    updatePeriodPos?: string;
    unknownArgs: string[];
  } => {
    const rawArgs = argsText.trim().length ? splitTopLevelByComma(argsText).map((s) => s.trim()) : [];
    const result: {
      modeToken?: string;
      autoTime?: string;
      driverTime?: string;
      autonomousAction?: string;
      updatePeriodPos?: string;
      unknownArgs: string[];
    } = { unknownArgs: [] };
    const getRhs = (s: string) => s.split('=').slice(1).join('=').trim();
    rawArgs.forEach((arg, idx) => {
      if (/=/.test(arg)) {
        if (new RegExp(`^${ARG_KEYS.MODE}\\s*=`).test(arg)) result.modeToken = getRhs(arg);
        else if (new RegExp(`^${ARG_KEYS.UPDATE_PERIOD}\\s*=`).test(arg)) result.unknownArgs.push(arg);
        else if (new RegExp(`^${ARG_KEYS.AUTO_MATCH_TIME}\\s*=`).test(arg)) result.autoTime = getRhs(arg);
        else if (new RegExp(`^${ARG_KEYS.DRIVER_MATCH_TIME}\\s*=`).test(arg)) result.driverTime = getRhs(arg);
        else if (new RegExp(`^${ROBOT_PROP.AUTONOMOUS_ACTION}\\s*=`).test(arg)) result.autonomousAction = getRhs(arg);
        else result.unknownArgs.push(arg);
      } else {
        // positional: keep only potential update period (first non-mode positional)
        if (idx === 0 && !result.modeToken) result.modeToken = arg;
        else if (!result.updatePeriodPos) result.updatePeriodPos = arg;
        // ignore further positional args (likely times) so we can rewrite as keywords
      }
    });
    return result;
  };

  /** Build the start() call argument string from mode and optional match times. */
  const buildStartArgsString = (
    desiredModeToken: string,
    opts: {
      unsupportedAutonomousActionCode?: string;
      autoMatchTime?: string;
      driverMatchTime?: string;
      autonomousAction?: string;
      updatePeriodPos?: string;
      unknownArgs?: string[];
    } = {}
  ): string => {
    const parts: string[] = [];
    parts.push(`${ARG_KEYS.MODE}=${desiredModeToken}`);
    if (opts.updatePeriodPos) parts.push(opts.updatePeriodPos);
    (opts.unknownArgs || []).forEach((a) => parts.push(a));
    const autonomousAction = opts.autonomousAction ?? opts.unsupportedAutonomousActionCode;
    if (/\.(AUTONOMOUS|MATCH)\s*$/.test(desiredModeToken) && autonomousAction) {
      parts.push(`${ROBOT_PROP.AUTONOMOUS_ACTION}=${autonomousAction}`);
    }
    if (/\.MATCH\s*$/.test(desiredModeToken)) {
      parts.push(`${ARG_KEYS.AUTO_MATCH_TIME}=${opts.autoMatchTime ?? signatureDefaultsRef.current.auto}`);
      parts.push(`${ARG_KEYS.DRIVER_MATCH_TIME}=${opts.driverMatchTime ?? signatureDefaultsRef.current.driver}`);
    }
    return parts.join(', ');
  };

  const buildProgramStartActionLines = (actions: ProgramStartActionConfig[], indent: string): string[] =>
    actions
      .flatMap((action) => {
        const expression = formatActionExpression(action.selection, action.argValues, resolveActionDef, { unsupportedCode: action.rawExpression });
        if (!expression) return [];
        return formatGeneratedPythonCall(`UtilityActions.add_action_at_program_start(${expression})`)
          .split('\n')
          .map((line) => `${indent}${line}`);
      })
      .filter((line) => line.length > 0);

  const buildStartArgsTextEdits = (
    code: string,
    opts: {
      nextDriveMode?: string;
      driveControlTimeStr?: string;
      autonomousTimeStr?: string;
      programStartActions?: ProgramStartActionConfig[];
      autonomousRoutineId?: string;
      autonomousActionExpression?: string;
    } = {}
  ): TextEditOperation[] => {
    const allRanges = findAllStartCallRangesInCode(code);
    const programStartActionRanges = findProgramStartActionCallRangesInCode(code);
    const nextProgramStartActions = opts.programStartActions ?? programStartActions;

    const sanitizeNum = (s?: string) => {
      if (s === undefined) return undefined;
      const v = parseFloat(s);
      return Number.isFinite(v) ? String(v) : undefined;
    };
    const fallbackAuto = signatureDefaultsRef.current.auto;
    const fallbackDriver = signatureDefaultsRef.current.driver;

    const edits: TextEditOperation[] = [];

    programStartActionRanges
      .sort((a, b) => b.startLine - a.startLine)
      .forEach((range) => {
        edits.push({
          startLine: range.startLine,
          startColumn: 1,
          endLine: range.endLine + 1,
          endColumn: 1,
          text: '',
        });
      });

    if (allRanges.length > 1) {
      allRanges
        .slice(1)
        .sort((a, b) => b.startLine - a.startLine)
        .forEach((range) => {
          edits.push({
            startLine: range.startLine,
            startColumn: 1,
            endLine: range.argsEndLine + 1,
            endColumn: 1,
            text: '',
          });
        });
    }

    const startRange = allRanges.length > 0 ? allRanges[0] : null;

    if (startRange) {
      const { argsText, startLine, argsStartCol, argsEndLine, argsEndCol } = startRange;
      const parsed = parseStartArgs(argsText);
      const currentModeToken = parsed.modeToken || '';
      const desiredModeToken = opts.nextDriveMode
        ? mapDriveModeToModeToken(opts.nextDriveMode)
        : currentModeToken || mapDriveModeToModeToken(driveMode);
      const autonomousAction =
        opts.autonomousActionExpression ??
        (opts.autonomousRoutineId !== undefined ? getAutonomousActionExpressionForRoutineId(opts.autonomousRoutineId) : parsed.autonomousAction);
      const newArgs = buildStartArgsString(desiredModeToken, {
        unsupportedAutonomousActionCode:
          (desiredModeToken === RUNMODE.AUTONOMOUS || desiredModeToken === RUNMODE.MATCH) && isUnsupportedLegacyAutonomousAction
            ? rawAutonomousActionCode
            : undefined,
        autoMatchTime: sanitizeNum(opts.autonomousTimeStr) ?? parsed.autoTime ?? fallbackAuto,
        driverMatchTime: sanitizeNum(opts.driveControlTimeStr) ?? parsed.driverTime ?? fallbackDriver,
        autonomousAction,
        updatePeriodPos: parsed.updatePeriodPos,
        unknownArgs: parsed.unknownArgs,
      });

      edits.push({
        startLine,
        startColumn: argsStartCol,
        endLine: argsEndLine,
        endColumn: argsEndCol,
        text: newArgs,
      });

      const startLineContent = splitCodeLines(code)[startLine - 1] ?? '';
      const indent = startLineContent.match(/^\s*/)?.[0] ?? '';
      const programStartLines = buildProgramStartActionLines(nextProgramStartActions, indent);
      if (programStartLines.length > 0) {
        edits.push({
          startLine,
          startColumn: 1,
          endLine: startLine,
          endColumn: 1,
          text: `${programStartLines.join('\n')}\n`,
        });
      }

      return edits;
    }

    const desiredModeToken = mapDriveModeToModeToken(opts.nextDriveMode ?? driveMode);
    const newArgs = buildStartArgsString(desiredModeToken, {
      unsupportedAutonomousActionCode:
        (desiredModeToken === RUNMODE.AUTONOMOUS || desiredModeToken === RUNMODE.MATCH) && isUnsupportedLegacyAutonomousAction
          ? rawAutonomousActionCode
          : undefined,
      autoMatchTime: sanitizeNum(opts.autonomousTimeStr ?? autonomousTime) ?? fallbackAuto,
      driverMatchTime: sanitizeNum(opts.driveControlTimeStr ?? driveControlTime) ?? fallbackDriver,
      autonomousAction:
        opts.autonomousActionExpression ??
        (opts.autonomousRoutineId !== undefined
          ? getAutonomousActionExpressionForRoutineId(opts.autonomousRoutineId)
          : getAutonomousActionExpressionForRoutineId(selectedAutonomousRoutineId)),
    });

    const lines = splitCodeLines(code);
    const lineCount = lines.length;
    const lastLine = lineCount >= 1 ? lines[lineCount - 1] ?? '' : '';
    const programStartLines = buildProgramStartActionLines(nextProgramStartActions, '');
    const blockLines = [...programStartLines, `RobotBase().start(${newArgs})`];
    const insertText = lastLine.trimEnd().length === 0 ? `${blockLines.join('\n')}\n` : `\n${blockLines.join('\n')}\n`;

    edits.push({
      startLine: lineCount >= 1 ? lineCount : 1,
      startColumn: lastLine.length + 1,
      endLine: lineCount >= 1 ? lineCount : 1,
      endColumn: lastLine.length + 1,
      text: insertText,
    });

    return edits;
  };

  const updateStartArgsInText = (
    code: string,
    opts: {
      nextDriveMode?: string;
      driveControlTimeStr?: string;
      autonomousTimeStr?: string;
      programStartActions?: ProgramStartActionConfig[];
      autonomousRoutineId?: string;
      autonomousActionExpression?: string;
    } = {}
  ): string => applyTextEditsToCode(code, buildStartArgsTextEdits(code, opts));

  const handleAutonomousRoutineSelect = (routineId: string) => {
    if (readOnly) return;
    setSelectedAutonomousRoutineId(routineId);
    updateStartArgsInCode({ autonomousRoutineId: routineId });
  };

  const openCreateRoutineDialog = () => {
    if (readOnly) return;
    setRoutineDialogMode('create');
    setRoutineDialogSourceId(null);
    setRoutineDialogBaseline(null);
    const existingNames = new Set(autonomousRoutines.map((routine) => routine.identifier));
    setRoutineDraftName(makeUniqueRoutineIdentifier('autonomous_routine', existingNames));
    setRoutineDraftComposition(createEmptyGroupStep());
    setRoutineDialogOpen(true);
  };

  const openEditRoutineDialog = () => {
    if (readOnly || !selectedAutonomousRoutine?.editable || !selectedAutonomousRoutine.composition) return;
    setRoutineDialogMode('edit');
    setRoutineDialogSourceId(selectedAutonomousRoutine.id);
    // Normalize before snapshotting the baseline so an expanded aggregate literal doesn't read as a
    // pending change on open.
    const normalizedComposition = normalizeRoutineComposition(selectedAutonomousRoutine.composition);
    setRoutineDialogBaseline(snapshotRoutineDraft(selectedAutonomousRoutine.identifier, pruneRoutineStep(normalizedComposition)));
    setRoutineDraftName(selectedAutonomousRoutine.identifier);
    setRoutineDraftComposition(normalizedComposition);
    setRoutineDialogOpen(true);
  };

  const openDuplicateRoutineDialog = () => {
    if (readOnly || !selectedAutonomousRoutine?.duplicable || !selectedAutonomousRoutine.composition) return;
    const existingNames = new Set(autonomousRoutines.map((routine) => routine.identifier));
    setRoutineDialogMode('duplicate');
    setRoutineDialogSourceId(selectedAutonomousRoutine.id);
    setRoutineDialogBaseline(null);
    setRoutineDraftName(makeUniqueRoutineIdentifier(`${selectedAutonomousRoutine.identifier}_copy`, existingNames));
    setRoutineDraftComposition(normalizeRoutineComposition(selectedAutonomousRoutine.composition));
    setRoutineDialogOpen(true);
  };

  const closeRoutineDialog = () => {
    setRoutineDialogOpen(false);
  };

  const handleRoutineStepChange = (path: number[], updater: (target: AutonomousRoutineStep) => AutonomousRoutineStep) => {
    setRoutineDraftComposition((prev) => updateStepAtPath(prev, path, updater));
  };

  const handleRoutineStepAppend = (path: number[], child: AutonomousRoutineStep) => {
    setRoutineDraftComposition((prev) => appendChildAtPath(prev, path, child));
  };

  const handleRoutineStepDelete = (path: number[]) => {
    setRoutineDraftComposition((prev) => removeStepAtPath(prev, path));
  };

  const handleRoutineStepMove = (path: number[], destination: RoutineStepDropPosition) => {
    setRoutineDraftComposition((prev) => moveStepToPosition(prev, path, destination));
  };

  const commitRoutineDialog = () => {
    if (readOnly || !routineDialogCanSave || !prunedRoutineDraftComposition) return;
    const editor = getEditor();
    const model = editor?.getModel();
    if (!model) return;
    const currentCode = getModelText(model);
    const currentRoutines = scanAutonomousRoutinesFromCode(currentCode);
    const sourceRoutine = routineDialogSourceId ? currentRoutines.find((routine) => routine.id === routineDialogSourceId) : null;
    const existingNames = new Set([...currentRoutines.map((routine) => routine.identifier), ...collectTopLevelPythonIdentifiers(currentCode)]);
    if (routineDialogMode === 'edit' && sourceRoutine) existingNames.delete(sourceRoutine.identifier);
    const identifier = makeUniqueRoutineIdentifier(routineDraftName, existingNames);
    const routineSource = formatAutonomousRoutineAssignment(identifier, prunedRoutineDraftComposition, resolveActionDef);

    let nextCode = currentCode;
    if (routineDialogMode === 'edit' && sourceRoutine?.editable) {
      nextCode = replaceRoutineInCode(currentCode, sourceRoutine, routineSource);
    } else {
      nextCode = appendRoutineToCode(currentCode, routineSource);
    }

    const finalCode = updateStartArgsInText(nextCode, { autonomousRoutineId: identifier, autonomousActionExpression: identifier });
    replaceModelText(finalCode);
    setSelectedAutonomousRoutineId(identifier);
    setRoutineDialogOpen(false);
  };

  const handleDeleteSelectedRoutine = () => {
    if (readOnly || !selectedAutonomousRoutine?.deletable) return;
    const editor = getEditor();
    const model = editor?.getModel();
    if (!model) return;
    const currentCode = getModelText(model);
    const nextCode = deleteRoutineFromCode(currentCode, selectedAutonomousRoutine);
    const finalCode = updateStartArgsInText(nextCode, { autonomousRoutineId: DO_NOTHING_AUTONOMOUS_ROUTINE_ID });
    replaceModelText(finalCode);
    setSelectedAutonomousRoutineId(DO_NOTHING_AUTONOMOUS_ROUTINE_ID);
  };

  const updateStartArgsInCode = (
    opts: {
      nextDriveMode?: string;
      driveControlTimeStr?: string;
      autonomousTimeStr?: string;
      programStartActions?: ProgramStartActionConfig[];
      autonomousRoutineId?: string;
      autonomousActionExpression?: string;
    } = {}
  ) => {
    const editor = getEditor();
    const model = editor?.getModel();
    if (!editor || !model) return;

    const edits = buildStartArgsTextEdits(getModelText(model), opts).map((edit) => ({
      range: new Range(edit.startLine, edit.startColumn, edit.endLine, edit.endColumn),
      text: edit.text,
      forceMoveMarkers: true,
    }));
    ensureModeImport(model, edits);
    if (edits.length === 0) return;

    isProgrammaticEdit.current = true;
    editor.pushUndoStop();
    editor.executeEdits('robot.start.updateArgs', edits);
    editor.pushUndoStop();
    setTimeout(() => {
      isProgrammaticEdit.current = false;
    }, 0);
  };

  const openProgramStartDialog = () => {
    if (readOnly) return;
    setProgramStartDialogActions(
      programStartActions.length > 0
        ? programStartActions.map((action) => {
            const draft = createProgramStartDraft(action);
            // Expand an existing aggregate `lines=[(...)]` literal into editable row keys on load.
            return draft.selection ? { ...draft, argValues: normalizeArgValuesForAction(draft.selection, draft.argValues) } : draft;
          })
        : [createProgramStartDraft()]
    );
    setProgramStartDialogOpen(true);
  };

  const closeProgramStartDialog = () => {
    setProgramStartDialogOpen(false);
  };

  // Seed a newly selected action with one editable row per sequence-of-tuples argument so the
  // user doesn't have to click "Add" to get started. Rows can still be added/removed afterward.
  const initialArgValuesForSelection = useCallback(
    (selection: string): Record<string, string> => {
      const actionDef = findActionDefForLabel(allAvailableActionsForDef, selection);
      const seeded: Record<string, string> = {};
      (actionDef?.arguments || []).forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) Object.assign(seeded, defaultSequenceTupleRowArgValues(frameworkArg));
      });
      return seeded;
    },
    [allAvailableActionsForDef]
  );

  // Expand any aggregate `lines=[(...)]` literal in an existing action's argValues into editable
  // `lines.N.*` row keys (or preserve it as unsupported) so loading an existing program-start action
  // or routine step shows editable rows instead of "No rows yet" and doesn't drop the literal on edit.
  const normalizeRoutineComposition = useCallback(
    (step: AutonomousRoutineStep): AutonomousRoutineStep => {
      if (step.kind === 'action') {
        return step.selection ? { ...step, argValues: normalizeArgValuesForAction(step.selection, step.argValues) } : step;
      }
      return { ...step, children: step.children.map(normalizeRoutineComposition) };
    },
    [normalizeArgValuesForAction]
  );

  const handleProgramStartActionSelect = (draftId: string, selection: string) => {
    setProgramStartDialogActions((prev) =>
      prev.map((action) =>
        action.draftId === draftId
          ? {
              ...action,
              selection,
              argValues: normalizeArgValuesForAction(selection, initialArgValuesForSelection(selection)),
              rawExpression: undefined,
            }
          : action
      )
    );
  };

  const handleProgramStartActionArgChange = (draftId: string, name: string, value: string) => {
    setProgramStartDialogActions((prev) =>
      prev.map((action) =>
        action.draftId === draftId ? { ...action, argValues: updateArgValuesForAction(action.selection, action.argValues, name, value) } : action
      )
    );
  };

  const handleAddProgramStartAction = () => {
    setProgramStartDialogActions((prev) => [...prev, createProgramStartDraft()]);
  };

  const handleDeleteProgramStartDraft = (draftId: string) => {
    setProgramStartDialogActions((prev) => prev.filter((action) => action.draftId !== draftId));
  };

  const commitProgramStartActions = (next: ProgramStartActionDraft[]) => {
    if (readOnly) return;
    const normalized = next
      .filter((action) => action.selection || action.rawExpression)
      .map((action) => ({
        ...action,
        argValues: action.selection ? normalizeArgValuesForAction(action.selection, action.argValues) : {},
        rawExpression: action.selection ? undefined : String(action.rawExpression ?? '').trim() || undefined,
      }));
    setProgramStartActions(normalized);
    updateStartArgsInCode({ programStartActions: normalized });
  };

  const handleUpdateProgramStartActions = () => {
    commitProgramStartActions(programStartDialogActions);
    setProgramStartDialogOpen(false);
  };

  const handleDeleteProgramStartAction = (draftId: string) => {
    const next = programStartActions.filter((action) => action.draftId !== draftId);
    commitProgramStartActions(next);
  };

  // Sync UI state from Redux whenever the hardware parser updates startCallConfig.
  // This runs on mount (restoring state after tab navigation) and on any code change.
  useEffect(() => {
    if (isProgrammaticEdit.current) return;
    const editor = getEditor();
    const model = editor?.getModel();
    const code = model ? getModelText(model) : project?.program?.code ?? '';
    const routinesFromCode = scanAutonomousRoutinesFromCode(code);
    if (!startCallConfig) {
      setDriveMode(DRIVE_MODE.DRIVE_CONTROL);
      setAutonomousTime('');
      setDriveControlTime('');
      setProgramStartActions([]);
      setAutonomousRoutinesIfChanged(routinesFromCode);
      setSelectedAutonomousRoutineId(DO_NOTHING_AUTONOMOUS_ROUTINE_ID);
      setLegacyAutonomousActionSelection('');
      setRawAutonomousActionCode('');
      return;
    }
    const selectedRoutine = resolveSelectedRoutineIdFromStartConfig(
      routinesFromCode,
      startCallConfig.autoSelection,
      startCallConfig.autoArgValues,
      startCallConfig.autonomousActionExpression
    );
    setAutonomousRoutinesIfChanged(selectedRoutine.routines);
    setSelectedAutonomousRoutineId((prev) => (prev !== selectedRoutine.routineId ? selectedRoutine.routineId : prev));
    setDriveMode((prev) => (prev !== startCallConfig.driveMode ? startCallConfig.driveMode : prev));
    setAutonomousTime((prev) => (prev !== startCallConfig.autonomousTime ? startCallConfig.autonomousTime : prev));
    setDriveControlTime((prev) => (prev !== startCallConfig.driveControlTime ? startCallConfig.driveControlTime : prev));
    setLegacyAutonomousActionSelection((prev) => (prev !== startCallConfig.autoSelection ? startCallConfig.autoSelection : prev));
    setRawAutonomousActionCode((prev) =>
      prev !== (startCallConfig.rawAutonomousActionCode ?? '') ? startCallConfig.rawAutonomousActionCode ?? '' : prev
    );
    setProgramStartActions((prev) => {
      const next = startCallConfig.programStartActions.map((action: ProgramStartActionConfig) => createProgramStartDraft(action));
      const snapshot = (items: ProgramStartActionDraft[]) =>
        JSON.stringify(items.map((action) => ({ selection: action.selection, argValues: action.argValues, rawExpression: action.rawExpression })));
      if (snapshot(prev) === snapshot(next)) return prev;
      return next;
    });
  }, [
    createProgramStartDraft,
    getEditor,
    getModelText,
    project?.program?.code,
    resolveSelectedRoutineIdFromStartConfig,
    setAutonomousRoutinesIfChanged,
    startCallConfig,
  ]);

  const matchDurationSection =
    driveMode === DRIVE_MODE.MATCH ? (
      <>
        <Divider />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              <Trans i18nKey="driveModeConfig.match.autonomous-time">Autonomous time:</Trans>
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('driveModeConfig.match.time-seconds', 'time (seconds)')}
              value={autonomousTime}
              onChange={handleAutonomousTimeChange}
              sx={matchTimeFieldSx}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              <Trans i18nKey="driveModeConfig.match.drive-control-time">Drive control time:</Trans>
            </Typography>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('driveModeConfig.match.time-seconds', 'time (seconds)')}
              value={driveControlTime}
              onChange={handleDriveTimeChange}
              sx={matchTimeFieldSx}
            />
          </Stack>
        </Stack>
      </>
    ) : null;

  const autonomousRoutineSection =
    driveMode !== DRIVE_MODE.DRIVE_CONTROL ? (
      <>
        <Divider />
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              <Trans i18nKey="driveModeConfig.autonomousRoutines.title">Autonomous Routine</Trans>
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={addAutonomousRoutineLabel}>
                <span>
                  <IconButton aria-label={addAutonomousRoutineLabel} size="small" onClick={openCreateRoutineDialog} disabled={readOnly}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={editAutonomousRoutineLabel}>
                <span>
                  <IconButton
                    aria-label={editAutonomousRoutineLabel}
                    size="small"
                    onClick={openEditRoutineDialog}
                    disabled={readOnly || !selectedAutonomousRoutine?.editable}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={duplicateAutonomousRoutineLabel}>
                <span>
                  <IconButton
                    aria-label={duplicateAutonomousRoutineLabel}
                    size="small"
                    onClick={openDuplicateRoutineDialog}
                    disabled={readOnly || !selectedAutonomousRoutine?.duplicable}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={deleteAutonomousRoutineLabel}>
                <span>
                  <IconButton
                    aria-label={deleteAutonomousRoutineLabel}
                    size="small"
                    onClick={handleDeleteSelectedRoutine}
                    disabled={readOnly || !selectedAutonomousRoutine?.deletable}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel id="autonomous-routine-selector-label">
              {t('driveModeConfig.autonomousRoutines.selectorLabel', { defaultValue: 'Routine' })}
            </InputLabel>
            <Select
              labelId="autonomous-routine-selector-label"
              label={t('driveModeConfig.autonomousRoutines.selectorLabel', { defaultValue: 'Routine' })}
              value={selectedAutonomousRoutineId}
              onChange={(event) => handleAutonomousRoutineSelect(String(event.target.value))}
              disabled={readOnly}
            >
              <MenuItem value={DO_NOTHING_AUTONOMOUS_ROUTINE_ID}>
                {t('driveModeConfig.autonomousRoutines.doNothing', { defaultValue: 'Do Nothing' })}
              </MenuItem>
              {autonomousRoutines.map((routine) => (
                <MenuItem key={routine.id} value={routine.id}>
                  {getRoutineDisplayLabel(routine)}
                  {routine.source === 'code-only'
                    ? ` (${t('driveModeConfig.autonomousRoutines.codeOnlySuffix', { defaultValue: 'code-only' })})`
                    : ''}
                </MenuItem>
              ))}
            </Select>
            {selectedAutonomousRoutine?.source === 'code-only' && (
              <FormHelperText>
                {t('driveModeConfig.autonomousRoutines.codeOnlyHelp', {
                  defaultValue: 'This routine can be selected, but guided editing is disabled because it was edited in code.',
                })}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      </>
    ) : null;

  const unsupportedLegacyAutonomousActionStatus = isUnsupportedLegacyAutonomousAction ? (
    <Box
      role="status"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        py: 0.5,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
        <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.title">Custom code</Trans>
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
        <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.description">Unsupported custom code: {{ code: rawAutonomousActionCode }}</Trans>
      </Typography>
    </Box>
  ) : null;

  const programStartActionsSection = (
    <>
      <Divider />
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            <Trans i18nKey="driveModeConfig.programStartActions.title">Program Start Actions</Trans>
          </Typography>
          <Tooltip title={addProgramStartActionLabel}>
            <span>
              <IconButton aria-label={addProgramStartActionLabel} size="small" onClick={openProgramStartDialog} disabled={readOnly}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {programStartActions.length === 0 ? (
          unsupportedLegacyAutonomousActionStatus ?? (
            <Typography variant="body2" color="text.secondary">
              {t('driveModeConfig.programStartActions.none', { defaultValue: 'None' })}
            </Typography>
          )
        ) : (
          <Stack spacing={0.75}>
            {unsupportedLegacyAutonomousActionStatus}
            {programStartActions.map((action) => {
              const rawExpression = String(action.rawExpression ?? '').trim();
              const actionDisplay = action.selection || rawExpression || t('driveModeConfig.unsupportedAutonomousAction.title', 'Custom code');
              return (
                <Stack
                  key={action.draftId}
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {rawExpression && !action.selection ? (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                          <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.title">Custom code</Trans>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.description">
                            Unsupported custom code: {{ code: rawExpression }}
                          </Trans>
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" noWrap title={actionDisplay} sx={{ minWidth: 0 }}>
                        {actionDisplay}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip
                    title={t('driveModeConfig.programStartActions.editAria', {
                      action: actionDisplay,
                      defaultValue: 'edit {{action}}',
                    })}
                  >
                    <span>
                      <IconButton
                        aria-label={t('driveModeConfig.programStartActions.editAria', {
                          action: actionDisplay,
                          defaultValue: 'edit {{action}}',
                        })}
                        size="small"
                        onClick={openProgramStartDialog}
                        disabled={readOnly}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={t('driveModeConfig.programStartActions.deleteAria', {
                      action: actionDisplay,
                      defaultValue: 'delete {{action}}',
                    })}
                  >
                    <span>
                      <IconButton
                        aria-label={t('driveModeConfig.programStartActions.deleteAria', {
                          action: actionDisplay,
                          defaultValue: 'delete {{action}}',
                        })}
                        size="small"
                        onClick={() => handleDeleteProgramStartAction(action.draftId)}
                        disabled={readOnly}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        )}
        <Alert severity="info" sx={{ py: 0 }}>
          {driveMode === DRIVE_MODE.DRIVE_CONTROL
            ? t('driveModeConfig.programStartActions.driverOverlapWarning', {
                defaultValue:
                  'Startup actions are scheduled when the program starts. Long-running actions may still be running when driver control begins.',
              })
            : t('driveModeConfig.programStartActions.overlapWarning', {
                defaultValue:
                  'Startup actions are scheduled when the program starts. Long-running actions may still be running when autonomous or driver control begins.',
              })}
        </Alert>
      </Stack>
    </>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="column" spacing={2}>
        <Typography color="text.secondary" variant="body2">
          <Trans i18nKey={'driveModeConfig.subtitle'}>Select a mode for your drive unit:</Trans>
        </Typography>

        <FormControl>
          <RadioGroup value={driveMode} onChange={handleDriveMode}>
            {DRIVE_MODE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                sx={modeRadioLabelSx}
                control={<Radio size="small" />}
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <Trans i18nKey={opt.labelKey}>{opt.defaultLabel}</Trans>
                    </Typography>
                    <HelpPopover id={opt.popoverId} text={t(opt.infoKey, opt.infoDefault)} />
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        {driveMode === DRIVE_MODE.MATCH ? (
          <>
            {autonomousRoutineSection}
            {matchDurationSection}
            {programStartActionsSection}
          </>
        ) : (
          <>
            {autonomousRoutineSection}
            {programStartActionsSection}
          </>
        )}
      </Stack>

      <Dialog open={programStartDialogOpen} onClose={closeProgramStartDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('driveModeConfig.programStartActions.dialogTitle', { defaultValue: 'Edit Program Starting Actions' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {programStartDialogActions.map((action, index) => {
              const actionDef = findActionDefForLabel(allAvailableActionsForDef, action.selection);
              const effectiveActionDef = withResolvedMotorVelocityLimits(actionDef, action.argValues);
              const rawExpression = String(action.rawExpression ?? '').trim();
              return (
                <Stack key={action.draftId} spacing={1} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ActionSelector
                        value={action.selection}
                        onChange={(value) => handleProgramStartActionSelect(action.draftId, value)}
                        actions={programStartSelectableActions}
                        allowNone
                        noneLabel={t('controller.buttonMapping.none', { defaultValue: 'None' })}
                        ariaLabel={t('driveModeConfig.programStartActions.rowAria', {
                          index: index + 1,
                          defaultValue: 'program start action {{index}}',
                        })}
                      />
                    </Box>
                    <Tooltip
                      title={t('driveModeConfig.programStartActions.deleteRowAria', {
                        index: index + 1,
                        defaultValue: 'delete program start action {{index}}',
                      })}
                    >
                      <span>
                        <IconButton
                          aria-label={t('driveModeConfig.programStartActions.deleteRowAria', {
                            index: index + 1,
                            defaultValue: 'delete program start action {{index}}',
                          })}
                          size="small"
                          onClick={() => handleDeleteProgramStartDraft(action.draftId)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  {rawExpression && !action.selection ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                        <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.title">Custom code</Trans>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        <Trans i18nKey="driveModeConfig.unsupportedAutonomousAction.description">
                          Unsupported custom code: {{ code: rawExpression }}
                        </Trans>
                      </Typography>
                    </Box>
                  ) : null}
                  <FrameworkActionArgumentFields
                    actionDef={effectiveActionDef}
                    values={action.argValues}
                    onArgChange={(name, value) => handleProgramStartActionArgChange(action.draftId, name, value)}
                    onArgValuesChange={(next) =>
                      setProgramStartDialogActions((prev) => prev.map((a) => (a.draftId === action.draftId ? { ...a, argValues: next } : a)))
                    }
                    pathOptions={pathOptions}
                    enumOptions={enumOptions}
                    t={t}
                    layout="stack"
                    showUnits={false}
                    spacing={1}
                    readOnly={readOnly}
                  />
                </Stack>
              );
            })}
            <Button startIcon={<AddIcon />} onClick={handleAddProgramStartAction} variant="outlined">
              {t('driveModeConfig.programStartActions.addAction', { defaultValue: 'ADD ACTION' })}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProgramStartDialog}>{t('common.cancel', { defaultValue: 'CANCEL' })}</Button>
          <Button onClick={handleUpdateProgramStartActions} variant="contained" disabled={readOnly || !programStartDialogCanSave}>
            {t('common.update', { defaultValue: 'UPDATE' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routineDialogOpen} onClose={closeRoutineDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {routineDialogMode === 'edit'
            ? t('driveModeConfig.autonomousRoutines.editDialogTitle', { defaultValue: 'Edit Autonomous Routine' })
            : routineDialogMode === 'duplicate'
            ? t('driveModeConfig.autonomousRoutines.duplicateDialogTitle', { defaultValue: 'Duplicate Autonomous Routine' })
            : t('driveModeConfig.autonomousRoutines.createDialogTitle', { defaultValue: 'Create Autonomous Routine' })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label={t('driveModeConfig.autonomousRoutines.nameLabel', { defaultValue: 'Routine name' })}
              value={routineDraftName}
              onChange={(event) => setRoutineDraftName(event.target.value)}
              disabled={readOnly}
              helperText={t('driveModeConfig.autonomousRoutines.nameHelp', {
                defaultValue: 'The display label and Python function name are derived from this identifier.',
              })}
            />
            <RoutineStepEditor
              step={routineDraftComposition}
              path={[]}
              canDelete={false}
              actionList={editorActionList}
              pathOptions={pathOptions}
              enumOptions={enumOptions}
              readOnly={readOnly}
              t={t}
              normalizeArgValuesForAction={normalizeArgValuesForAction}
              initialArgValuesForSelection={initialArgValuesForSelection}
              onChange={handleRoutineStepChange}
              onAppendChild={handleRoutineStepAppend}
              onDelete={handleRoutineStepDelete}
              onMove={handleRoutineStepMove}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoutineDialog}>{t('common.cancel', { defaultValue: 'CANCEL' })}</Button>
          <Button onClick={commitRoutineDialog} variant="contained" disabled={readOnly || !routineDialogCanSave}>
            {t('common.save', { defaultValue: 'SAVE' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriveModeConfigurationTab;
